import { gradeQuality } from './photoAligner';

let _detectorPromise = null;

// Exposed only for tests — resets the singleton so each test gets a fresh setup.
export function _resetDetectorForTests() { _detectorPromise = null; }

async function getDetector() {
  if (_detectorPromise) return _detectorPromise;
  _detectorPromise = (async () => {
    const { FilesetResolver, PoseLandmarker } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm'
    );
    return PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        // Full model (~11 MB) instead of Lite (~3 MB) — dramatically better
        // landmark confidence scores on bare-chested / low-contrast subjects.
        // Dynamic-imported so this does not affect the main bundle.
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task',
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      numPoses: 1,
    });
  })().catch((err) => {
    _detectorPromise = null;
    throw err;
  });
  return _detectorPromise;
}

async function loadImageElement(input) {
  if (typeof input === 'string') {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = input;
    });
  }
  return input; // HTMLImageElement, HTMLVideoElement, HTMLCanvasElement
}

export async function detectKeypoints(input, poseType) {
  try {
    const detector = await getDetector();
    const imageEl = await loadImageElement(input);
    const result = detector.detect(imageEl);
    const first = result?.landmarks?.[0];
    const firstWorld = result?.worldLandmarks?.[0];

    if (!first || first.length === 0) {
      return {
        keypoints: null,
        poseType,
        quality: 'fail',
        reason: "Couldn't detect your body — check lighting and framing",
      };
    }

    // Some MediaPipe configurations populate visibility only on worldLandmarks,
    // not on the normalized landmarks. Mirror it across so downstream gating in
    // photoAligner.detected() can use the real confidence score when present.
    const merged = first.map((kp, i) => {
      const wkp = firstWorld && firstWorld[i];
      if (typeof kp.visibility !== 'number' && typeof wkp?.visibility === 'number') {
        return { ...kp, visibility: wkp.visibility };
      }
      return kp;
    });

    const grading = gradeQuality(merged, poseType);
    if (grading.quality === 'fail' && /shoulders/i.test(grading.reason || '')) {
      // Dev observability — when shoulder detection fails despite a clearly-
      // visible body, log the actual landmark coordinates and visibility so
      // future tuning has numbers to work from.
      // eslint-disable-next-line no-console
      console.debug('[poseDetector] shoulder fail — landmark info:', {
        poseType,
        leftShoulder: { x: merged[11]?.x?.toFixed(3), y: merged[11]?.y?.toFixed(3), vis: merged[11]?.visibility?.toFixed?.(3) ?? 'n/a' },
        rightShoulder: { x: merged[12]?.x?.toFixed(3), y: merged[12]?.y?.toFixed(3), vis: merged[12]?.visibility?.toFixed?.(3) ?? 'n/a' },
      });
    }
    return {
      keypoints: merged,
      poseType,
      quality: grading.quality,
      reason: grading.reason,
    };
  } catch (err) {
    // Model failed to load (network / WASM) → propagate as 'fail' without throwing
    return {
      keypoints: null,
      poseType,
      quality: 'fail',
      reason: 'Pose detection unavailable',
    };
  }
}
